import type { ViewModule } from "./types";

// Every view of the one-page app. Each is its own chunk, loaded on first use.
export const ROUTES: Partial<Record<string, () => Promise<ViewModule>>> = {
  "/": () => import("./views/home/front.js"),           // the front page (#2073)
  "/film/": () => import("./views/home/index.js"),     // the film (the intro), which ends in the lab
  "/lab/": () => import("./views/lab/index.js"),
  "/city/": () => import("./views/lab/index.js"),   // the city map: the lab's own view, above the city (owner, #174)
  "/classroom/": () => import("./views/classroom/index.js"),   // where teachers run sessions with a group (#2564)
  "/join/": () => import("./views/classroom/join.js"),   // where a learner joins a live session with its code (#2564)
  "/projects/cp-001/": () => import("./views/project/cp-001/index.js"),
  "/projects/bs-001/": () => import("./views/project/bs-001/index.js"),
  "/projects/cp-002/": () => import("./views/project/cp-002/index.js"),
  "/projects/mcc-001/": () => import("./views/project/mcc-001/index.js"),
  "/projects/mcc-002/": () => import("./views/project/mcc-002/index.js"),   // unlisted until the owner opens it (#2700)
  "/sites/datacentre/": () => import("./views/sites/datacentre/index.js"),
  "/sites/bess/": () => import("./views/sites/bess/index.js"),
  "/sites/works/": () => import("./views/sites/works/index.js")
};

/** The city's sites whose worlds are being built (#512), by their key on the city map. Each route opens its world's own view as soon as
 *  views/sites/<key>/index.js exists (that file is all a world adds), and says "coming soon" until then. */
export const NEW_SITES: Record<string, { label: string; enter: string }> = {
  solar: { label: "Solar", enter: "Enter the solar park" }, wind: { label: "Wind", enter: "Enter the wind farm" },
  ev: { label: "EV charging", enter: "Enter the charging hub" }, buildings: { label: "Buildings", enter: "Enter the block" },
  utilities: { label: "Grid", enter: "Enter the substation" }
};
const SITE_VIEWS = import.meta.glob<ViewModule>("./views/sites/{solar,wind,ev,buildings,utilities}/index.js");
const siteView = (key: string) => SITE_VIEWS[`./views/sites/${key}/index.js`];
/** Whether a new site's own view is in the build (the lab shows its way in only then). */
export const siteBuilt = (key: string) => !!siteView(key);
for (const [key, { label }] of Object.entries(NEW_SITES))
  ROUTES[`/sites/${key}/`] = siteView(key) ?? (() => import("./views/lab/coming-soon").then(m => ({ default: () => m.comingSoon(label) })));

/** The deploy part each route belongs to (build/deploy-info.mjs names the same parts). */
export const partOf = (path: string) => path.startsWith("/projects/") ? path.split("/")[2].toUpperCase() : path.startsWith("/sites/bess/") ? "SITE-BESS" : path.startsWith("/sites/works/") ? "SITE-WORKS" : path.startsWith("/sites/ev/") ? "SITE-EV" : path.startsWith("/sites/solar/") ? "SITE-SOLAR" : path.startsWith("/sites/wind/") ? "SITE-WIND" : path.startsWith("/sites/utilities/") ? "SITE-GRID" : "home";

/** "/lab" and "/lab/index.html" both mean "/lab/". */
export function normalise(path: string): string {
  path = path.replace(/index\.html$/, "");
  return path.endsWith("/") ? path : path + "/";
}
